/* ═══════════════════════════════════════════════════════════════════════════════
   Claims Report Payments Tour (v2.4.0)

   Interactive tour that programmatically opens the payment popover and walks
   the user through each payment type option:
   1. Paid by Builder column → what it shows
   2. + Add Payment cell → click to open menu (tour opens it automatically)
   3. Progress Payment option → subcategory-level tracking
   4. Variations option → per-docket tracking, Dashpivot sync
   5. Retention option → retention release tracking
   6. Full Reconciliation option → view all sections at once

   ARCHITECTURE:
   - onEnter opens the popover by clicking the first cell
   - pointer-events: none disables the popover buttons during the tour
   - onEnter on completion closes the popover and restores pointer-events
   - skipOverlay: true prevents the tour overlay from hiding the popover
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

// ─── Helpers to programmatically open / close the payment popover ────────────

function openPaymentPopover() {
  // Guard: already open
  if (document.querySelector('[data-tour-id="cr-payment-popover"]')) {
    // Already open, just lock it
    lockPopover();
    return;
  }
  // Click the first "+ Add Payment" cell to trigger the popover
  const cell = document.querySelector('[data-tour-id="cr-paid-builder-cell"]') as HTMLElement;
  cell?.click();
  // After React state update + popover mount, disable pointer events on the popover
  // so users can't accidentally click a payment option during the tour
  setTimeout(lockPopover, 600);
}

function lockPopover() {
  const popover = document.querySelector('[data-tour-id="cr-payment-popover"]') as HTMLElement;
  if (popover) {
    popover.style.pointerEvents = 'none';
  }
  // Also lock the backdrop so clicking outside doesn't close it
  const backdrop = popover?.previousElementSibling as HTMLElement;
  if (backdrop) {
    backdrop.style.pointerEvents = 'none';
  }
}

function closePaymentPopover() {
  const popover = document.querySelector('[data-tour-id="cr-payment-popover"]') as HTMLElement;
  if (popover) {
    // Restore pointer events
    popover.style.pointerEvents = '';
    // Restore backdrop pointer events and click to close
    const backdrop = popover.previousElementSibling as HTMLElement;
    if (backdrop) {
      backdrop.style.pointerEvents = '';
      backdrop.click();
    }
  }
}

// ─── Tour Definition ─────────────────────────────────────────────────────────

export const claimsReportPaymentsTour: TourDefinition = {
  id: 'claims_report_payments_v2_4',
  version: '2.4.0',
  type: 'feature_update',
  audience: 'all',
  page: 'claims_reports',
  priority: 1,
  title: 'Add Payment',
  subtitle: 'Track builder payments across progress, variations, and retention.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'Updated: Add Payment',
      description: 'You can now record and track builder payments directly from the claims report table. Each claim can be broken down into progress, variations, and retention payments. Let\'s walk through how it works.',
      icon: '💰',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Paid by Builder Column ───────────────────────────────
    {
      id: 'paid-builder-header',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-paid-builder-header"]',
      position: 'bottom',
      title: 'Paid by Builder',
      description: 'This column shows the total amount the builder has paid for each claim period. It combines all three payment types (progress, variations, and retention) into one figure with a progress bar.',
      icon: '📊',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 2: Add Payment Cell ─────────────────────────────────────
    {
      id: 'add-payment-cell',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-paid-builder-cell"]',
      position: 'left',
      title: 'Click to Add Payment',
      description: 'Click any row in this column to open the payment type selector. When no payment has been recorded, it shows "+ Add Payment". After payments are saved, it displays the total with a percentage bar.',
      icon: '➕',
      actionLabel: 'Next',
      spotlightPadding: 10,
    },

    // ── Step 3: Payment Popover (opened programmatically) ────────────
    {
      id: 'payment-popover',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-payment-popover"]',
      position: 'left',
      title: 'Payment Type Selector',
      description: 'Choose which type of payment to record. Each option opens a dedicated panel where you can set the action and amount for each line item. Let\'s go through each one.',
      icon: '📋',
      actionLabel: 'Next',
      spotlightPadding: 6,
      skipOverlay: true,
      onEnter: openPaymentPopover,
    },

    // ── Step 4: Progress Payment Option ──────────────────────────────
    {
      id: 'option-progress',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-option-progress"]',
      position: 'left',
      title: '① Progress Payment',
      description: 'Tracks payment at the subcategory level. Each contract subcategory can be set to:\n\n• Paid: auto-fills 100% of claimed\n• Agreement Made: enter a custom partial amount\n• Voided: rejected, sets to $0\n• Works Not Completed: pushes to next month\'s claim',
      icon: '📈',
      actionLabel: 'Next',
      spotlightPadding: 4,
      skipOverlay: true,
    },

    // ── Step 5: Variations Option ────────────────────────────────────
    {
      id: 'option-variations',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-option-variations"]',
      position: 'left',
      title: '② Variations',
      description: 'Tracks payment per variation docket synced from Dashpivot. Each approved variation appears as a line item. Set it to Paid, Agreement Made, or Voided. Outstanding variations from previous months are also included.',
      icon: '📋',
      actionLabel: 'Next',
      spotlightPadding: 4,
      skipOverlay: true,
    },

    // ── Step 6: Retention Option ─────────────────────────────────────
    {
      id: 'option-retention',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-option-retention"]',
      position: 'left',
      title: '③ Retention Release',
      description: 'Records retention paid back against the project outstanding retention balance. It does not allocate payment back to the oldest held rows.',
      icon: '🔒',
      actionLabel: 'Next',
      spotlightPadding: 4,
      skipOverlay: true,
    },

    // ── Step 7: Full Reconciliation Option ───────────────────────────
    {
      id: 'option-full',
      type: 'spotlight',
      targetSelector: '[data-tour-id="cr-option-full"]',
      position: 'left',
      title: '④ Full Reconciliation',
      description: 'Opens progress, variations, and retention release in a single view. Use this when you want to reconcile everything at once instead of opening each panel separately.',
      icon: '💵',
      actionLabel: 'Got It',
      spotlightPadding: 4,
      skipOverlay: true,
    },

    // ── Step 8: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'Ready to Track Payments!',
      description: 'Click any row\'s Paid by Builder cell to get started. Choose Progress, Variations, or Retention to record payments. The totals and difference update automatically after saving.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
      onEnter: closePaymentPopover,
    },
  ],
};
